import React,{useEffect} from 'react';
import LayoutWrapper from '@iso/components/utility/layoutWrapper.js';
import Box from '@iso/components/utility/box';
import PageHeader from '@iso/components/utility/pageHeader';
import IntlMessages from '@iso/components/utility/intlMessages';
import { useSelector, useDispatch } from 'react-redux';
import { configs } from '@iso/components/Tables/configs';
import { Alert, Button, Dropdown, Menu, Select, DatePicker, Pagination, Input, Spin, Tag, Modal } from 'antd';
import FormPage from './Form.styles';
import { PlusOutlined, EditOutlined, SearchOutlined,FormOutlined,CloseCircleOutlined,EllipsisOutlined,FileOutlined,FileDoneOutlined,PaperClipOutlined, LoadingOutlined, TeamOutlined} from '@ant-design/icons';

import propertyActions from '@iso/redux/property/actions';
import _ from 'lodash';
import Table from '@iso/components/collapsing-table/table';
import FormModal from './FormModal';
import InspectionModal from './InspectionModal';
import ManageHostsModal from './ManageHostsModal';
import Loader from '@iso/components/utility/loader';
import { propertyService, formService } from '@iso/services';
import { getFormPurposeLabel } from '@iso/constants/formPurposes';
import { getFormTypeLabel } from '@iso/constants/formTypes';
const { RangePicker } = DatePicker;
const { changeActiveProperty } = propertyActions;

const Form = (props) => {
  const dispatch = useDispatch();
  const activePropertyId = useSelector((state) => state.property.activePropertyId);
  const activePropertySubscription = useSelector((state) => state.property.activePropertySubscription);
  const activePermission = useSelector((state) => state.permission.activePermission);
  const [loadingData, setLoadingData] = React.useState(true);
  const [visibleFormModal, setVisiblityFormModal] = React.useState(false);
  const [selectedDateRange, setSelectedDateRange] = React.useState([]);
  const { Option } = Select;
  const [properties, setProperties] = React.useState([]);
  const [allProperties, setAllProperties] = React.useState([]);
  const [loadingOptions, setLoadingOptions] = React.useState(false);
  const [hasMore, setHasMore] = React.useState(false);
  const [pageOptions, setPageOptions] = React.useState(1);
  const optionReqIdRef = React.useRef(0);
  const menuRef = React.useRef(null);
  const [isSaved, setIsSaved] = React.useState(false);
  const [forms, setForms] = React.useState([]);
  const [formData, setFormData] = React.useState({});
  const [visibleSelectProperty, setVisiblitySelectProperty] = React.useState(false);
  const [visibleInspectionModal, setVisibleInspectionModal] = React.useState(false);
  const [manageHostsVisible, setManageHostsVisible] = React.useState(false);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [searchInput, setsearchInput] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [totalCount, setTotalCount] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(5);
  const [coverageSlots, setCoverageSlots] = React.useState([]);
  const [loadingCoverage, setLoadingCoverage] = React.useState(false);
  const [showLegacySlots, setShowLegacySlots] = React.useState(false);
  const greenIndicator = <LoadingOutlined style={{ fontSize: 16, color: "#0B2A1B" }} spin />;

  // This page is also mounted inside the Topbar/Property Forms modal. There it is
  // already scoped to one property — the caller opened it FROM that property — so
  // the active-property switcher makes no sense in the modal (it rendered as a
  // stray property dropdown in the modal header, next to Add Form). The switcher is
  // the ONLY consumer of `properties` / `allProperties`, so hiding it also makes
  // both property-list requests dead weight; skip them rather than fire two extra
  // calls every time the modal opens.
  const embedded = !!props.isShowFormModal;

  const getOptionPropertyList = (pageArg = 1) => {
    if (loadingOptions || (!hasMore && pageArg !== 1)) return;

    setLoadingOptions(true);
    const myReq = ++optionReqIdRef.current;

    return propertyService.getOptionProperty(pageArg)
      .then((res) => {
        if (myReq !== optionReqIdRef.current) return;

        const list = Array.isArray(res?.properties?.data)
          ? res.properties.data
          : Array.isArray(res?.properties)
            ? res.properties
            : [];

        setAllProperties((prev) =>
          pageArg === 1 ? list : [...prev, ...list.filter(p => !prev.some(x => x.id === p.id))]
        );
        const totalPage = res?.properties?.total_page;
        const more = totalPage ? pageArg < totalPage : list.length > 0;
        setHasMore(more);
        if (more) {
          setPageOptions(pageArg + 1);
        } else if (pageArg === 1) {
          setPageOptions(1);
        }
      })
      .catch((err) => {
        console.error('Error loading property options:', err);
      })
      .finally(() => setLoadingOptions(false));
  };

  const handlePopupScroll = React.useCallback((e) => {
    const t = e.target;
    const nearBottom = t.scrollTop + t.clientHeight >= t.scrollHeight - 100;

    if (nearBottom && hasMore && !loadingOptions) {
      getOptionPropertyList(pageOptions);
    }
  }, [hasMore, loadingOptions, pageOptions, getOptionPropertyList]);

  // The server returns exactly one page now, so there is nothing left to slice.
  const displayedForms = forms;

  const handlePageSizeChange = (current, size) => {
    setCurrentPage(1);
    setPageSize(size);
  };
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // One page of forms + the filtered total, both from the server. Paging, search
  // and the date range are all server-side concerns now, so this re-runs whenever
  // any of them changes and there is no client-side filtering left.
  const updateFormData = React.useCallback(() => {
    if (!activePropertyId) {
      return;
    }
    setLoadingData(true);
    const params = { page: currentPage, page_size: pageSize };
    if (debouncedSearch) {
      params.search = debouncedSearch;
    }
    if (selectedDateRange && selectedDateRange.length === 2 && selectedDateRange[0] && selectedDateRange[1]) {
      params.from_date = selectedDateRange[0].format('YYYY-MM-DD 00:00:00');
      params.to_date = selectedDateRange[1].format('YYYY-MM-DD 23:59:59');
    }
    formService.getList(activePropertyId, params).then(res => {
      if (res && res.code === '0000') {
        setForms(res.forms || []);
        setTotalCount(res.total_count || 0);
      }
    }).finally(() => setLoadingData(false));
  }, [activePropertyId, currentPage, pageSize, debouncedSearch, selectedDateRange]);

  useEffect(() => {
    updateFormData();
  }, [updateFormData, isSaved, activePropertySubscription, props.isShowFormModal]);

  // Required-form coverage for the warning banner. Refetched when the property
  // changes and after a save (isSaved), so adding a missing form clears its slot
  // immediately. The endpoint returns an empty slot list for anyone who is not an
  // owner/admin, so no permission check is needed here to keep the banner hidden.
  const updateCoverage = React.useCallback(() => {
    if (!activePropertyId) {
      setCoverageSlots([]);
      return;
    }
    setLoadingCoverage(true);
    formService.getRequiredCoverage(activePropertyId)
      .then((res) => setCoverageSlots((res && res.slots) || []))
      .finally(() => setLoadingCoverage(false));
  }, [activePropertyId]);

  useEffect(() => {
    updateCoverage();
  }, [updateCoverage, isSaved]);

  // Debounce the search box so typing doesn't fire a request per keystroke, and
  // snap back to page 1 so matches aren't hidden past the end of the old page.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  React.useEffect(() => {
    if (embedded) {
      return;
    }
    getOptionPropertyList(1);
  }, [embedded]);

  useEffect(() => {
    if (embedded) {
      return;
    }
    propertyService.getProperties().then(res => {
      const rawList = res?.properties;
      const list = Array.isArray(rawList) ? rawList : rawList?.data ?? [];
      if(list.length === 0){
        setVisiblitySelectProperty(true);
        setProperties([]);
        setAllProperties([]);
        setHasMore(false);
        setPageOptions(1);
      }else{
        setProperties(list);
        setAllProperties(prev => {
          if (prev.length === 0) {
            return list;
          }
          const merged = [...prev];
          list.forEach(item => {
            if (item && !merged.some(p => p.id === item.id)) {
              merged.push(item);
            }
          });
          return merged;
        });
        if(list.length === 1){
          localStorage.setItem('active_property', JSON.stringify(list[0]));
          dispatch(changeActiveProperty(list[0]));
        }
      }
    }); 
  }, [activePropertyId, isSaved, activePropertySubscription, embedded]);

  const showModal = () => {
    setVisiblityFormModal(true);
  }

  const cancelModal = () => {
    setVisiblityFormModal(false);
    setFormData({});
  }

  const openEditModal = (form) => {
    setFormData(form);
    setVisiblityFormModal(true);
  }

  // Open Add Form preset to a missing slot's category. No `id` is set, so the
  // modal stays in create mode and treats this as a brand-new form.
  const openAddModalForSlot = (slot) => {
    if (!slot.category_global_form_id) {
      return;
    }
    setFormData({
      category_global_form_id: slot.category_global_form_id,
      purpose: slot.purpose || undefined,
    });
    setVisiblityFormModal(true);
  }

  const missingSlots = coverageSlots.filter((slot) => slot.status === 'missing');
  const legacySlots = coverageSlots.filter((slot) => slot.status === 'legacy');
  const canManageForms = activePermission === 'owner' || activePermission === 'admin';

  // Coverage warning. Two separate signals, deliberately weighted differently:
  // MISSING is actionable (warning, each name opens Add Form), LEGACY is only
  // informational — there is no migration path from a survey-engine form to a
  // schema one, so linking it would imply an action that does not exist. On an
  // established property almost every slot is legacy, so that half is collapsed
  // behind a toggle and never competes with the part you can act on.
  const renderCoverageBanner = () => {
    if (!canManageForms || loadingCoverage) {
      return null;
    }
    if (!missingSlots.length && !legacySlots.length) {
      return null;
    }

    const legacyToggle = legacySlots.length ? (
      <div style={{ marginTop: missingSlots.length ? 8 : 0 }}>
        <Button
          type="link"
          size="small"
          style={{ padding: 0, height: 'auto' }}
          onClick={() => setShowLegacySlots(!showLegacySlots)}
        >
          {showLegacySlots ? 'Hide' : 'Show'} {legacySlots.length} form
          {legacySlots.length === 1 ? '' : 's'} still on the legacy engine
        </Button>
        {showLegacySlots && (
          <div style={{ marginTop: 4, color: 'rgba(0, 0, 0, 0.55)' }}>
            {legacySlots.map((slot) => slot.label).join(', ')}
            {' — these are configured and working. Rebuilding them as schema forms is optional.'}
          </div>
        )}
      </div>
    ) : null;

    if (!missingSlots.length) {
      return (
        <div style={{ padding: '0 25px 12px' }}>
          {legacyToggle}
        </div>
      );
    }

    return (
      <div style={{ paddingBottom: '24px' }}>
        <Alert
          type="warning"
          showIcon
          message={
            <span>
              This property has not configured:{' '}
              {missingSlots.map((slot, index) => (
                <React.Fragment key={slot.key}>
                  {index > 0 && ', '}
                  {slot.category_global_form_id ? (
                    // A real button, not an <a> without href — it performs an action
                    // rather than navigating, and stays keyboard accessible.
                    <Button
                      type="link"
                      size="small"
                      style={{ padding: 0, height: 'auto', fontSize: 'inherit', verticalAlign: 'baseline' }}
                      onClick={() => openAddModalForSlot(slot)}
                    >
                      {slot.label}
                    </Button>
                  ) : (
                    <span>{slot.label}</span>
                  )}
                </React.Fragment>
              ))}
            </span>
          }
          description={legacyToggle}
        />
      </div>
    );
  }

  const openInspectionModal = (form) => {
    setFormData(form);
    setVisibleInspectionModal(true);
  }

  const cancelInspectionModal = () => {
    setFormData({});
    setVisibleInspectionModal(false);
  }

  // const menuForm = (property, permission) => (  
  //   <Menu onClick={handleMenuClick.bind(this, property)}>
  //     {(permission == "owner" || permission == "admin") && <Menu.Item key="addForm" icon={<PlusCircleOutlined />}>
  //       <IntlMessages id="propertyPage.managePage.addForm" />
  //     </Menu.Item>}
  //     {permission == "owner" && <Menu.Item key="viewForm" icon={<EyeOutlined />}>
  //       <IntlMessages id="propertyPage.managePage.viewForm" />
  //     </Menu.Item>}
  //   </Menu>
  // );

  const appendBackLink = (url, backLink) => {
    if (!url || !backLink) {
      return url;
    }
    try {
      const parsed = new URL(url);
      if (!parsed.searchParams.has('back_link')) {
        parsed.searchParams.set('back_link', backLink);
      }
      return parsed.toString();
    } catch (error) {
      if (url.includes('back_link=')) {
        return url;
      }
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}back_link=${encodeURIComponent(backLink)}`;
    }
  };

  const exportSubmissions = (form) => {
    formService.exportSubmissions(form.id).then(res => {
      if(res.code === '0000'){
        window.location.href = res.file_path
      }
    }); 
  }
  const menu = (form) => (
    <Menu onClick={handleMenuClick.bind(this, form)}>
      <Menu.Item key="editDetails" icon={<EditOutlined />}>
      <IntlMessages id="formPage.table.button.edit" />
      </Menu.Item>

      <Menu.Item key="submit" icon={<FormOutlined/>}>
      <IntlMessages id="formPage.table.button.submit" />
      </Menu.Item>

      <Menu.Item key="report" icon={<FileOutlined/>}>
      <IntlMessages id="formPage.table.button.report" />
      </Menu.Item>

      <Menu.Item key="Submissions" icon={<FileDoneOutlined />}>
      <IntlMessages id="Submissions" />
      </Menu.Item>


    </Menu>
  );
  const handleMenuClick = (form, e) => {
    if (e.key === 'editDetails') {
      openEditModal(form)
    }

    if (e.key === 'submit') {
      // A private form has no public link to open — it previously fell through to
      // the edit modal, which looks like the Submit action doing nothing useful.
      // Say why instead. Checked before public_form_url so a public form that is
      // merely missing its token still uses the existing fallback below.
      if (!form.is_public) {
        Modal.warning({
          title: 'This form is private',
          content: (
            <span>
              This form is private, you cannot open form to submit.
              <br />
              Please public this form to allow submit!
            </span>
          ),
          okText: 'OK',
        });
        return;
      }
      if (form.public_form_url) {
        const urlWithBackLink = appendBackLink(form.public_form_url, 'manage-forms');
        window.open(urlWithBackLink, '_blank').focus();
      } else {
        openEditModal(form)
      }
    }

    if (e.key === 'report') {
      exportSubmissions(form)
    }

    if (e.key === "Submissions") {
      openInspectionModal(form) 
    }
  }
  const columns = [
    {
      label: <IntlMessages id="formPage.table.title.formName" />,
      accessor: 'name',
      minWidth: 300,
      sortable: true,
      position: 1,
      priorityLevel: 1,
      CustomComponent: (cell) => {
        let form = cell.row;

        const hasImages = form && form.surveyQuestions && form.surveyQuestions.length > 0
        && form.surveyQuestions[0].images && form.surveyQuestions[0].images.length > 0; // Check if images exist in surveyQuestions
        const hasAttachment = form && form.files;
        // schema_json present -> new schema engine; absent -> legacy survey form
        // (still fully supported, unchanged). Mark legacy forms so they're clear.
        const isSchema = form && form.schema_json && Array.isArray(form.schema_json.steps) && form.schema_json.steps.length > 0;
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            <div style={{ marginLeft: hasAttachment ? "10px" : "0px" }}>
              {form.name}
            </div>
            {hasAttachment.length && hasAttachment.length > 0 || hasImages ? (
              <PaperClipOutlined style={{ color: "#0B2A1B" }} />
            ) : (
              ""
            )}
            {!isSchema && (
              <Tag color="default" style={{ marginLeft: 8 }}>Legacy</Tag>
            )}
          </div>
        );
      },
    },
    {
      label: <IntlMessages id="Form Category" />,
      accessor: 'category_global_form_id',
      minWidth: 150,
      sortable: false,
      position: 2,
      priorityLevel: 2,
      CustomComponent: cell => {
        let form = cell.row;
        // New forms carry a library Category; legacy forms fall back to their old `type`.
        const categoryName = form.category_global_form_data && form.category_global_form_data.name;
        return categoryName || (form.type ? getFormTypeLabel(form.type) : '-');
      }
    },
    {
      label: <IntlMessages id="Form Purpose" />,
      accessor: 'purpose',
      minWidth: 150,
      sortable: true,
      position: 3,
      priorityLevel: 3,
      CustomComponent: cell => {
        let form = cell.row;
        return form.purpose ? getFormPurposeLabel(form.purpose) : '-';
      }
    },
    {
      label: 'Primary',
      accessor: 'is_primary',
      minWidth: 100,
      sortable: true,
      position: 4,
      priorityLevel: 4,
      CustomComponent: cell => {
        let form = cell.row;
        return form.is_primary == 1 ? <Tag color="green">Primary</Tag> : '-';
      }
    },
    {
      label: <IntlMessages id="formPage.table.title.formStatus" />,
      accessor: 'is_public',
      minWidth: 100,
      sortable: true,
      position: 5,
      priorityLevel: 5,
      CustomComponent: cell => {
        let form = cell.row

        if(form.is_public == 0){
          return 'Private';
        }else{
          return 'Public';
        }
      }
    },
    {
      label: <IntlMessages id="Created Date" />,
      accessor: 'date',
      minWidth: 150,
      sortable: true,
      position: 6,
      priorityLevel: 6,
    },
    {
      label: <IntlMessages id="formPage.table.title.lastUpdatedAt" />,
      accessor: 'updated_at',
      minWidth: 150,
      sortable: true,
      position: 7,
      priorityLevel: 7,
    },
    {
      label: <IntlMessages id="formPage.table.title.lastActivityAt" />,
      accessor: 'last_activity',
      minWidth: 150,
      sortable: true,
      position: 8,
      priorityLevel: 8,
    },
    {
      label: '',
      accessor: 'actions',
      minWidth: 80,
      sortable: true,
      position: 9,
      priorityLevel: 9,
      noTitle: true,
      CustomComponent: cell => {
        let form = cell.row;

        return (
          <Dropdown overlay={menu(form)} trigger={["click"]}>
          <div
            className="property-area-dot-btn"
            style={{ cursor: "pointer" }}
          >
            <EllipsisOutlined className="dotIcon"/>
          </div>
        </Dropdown>
        );
      }
    },
  ];

  const handleOptionClick = (selectedValue) => {
    const propertyId = typeof selectedValue === 'object' && selectedValue !== null
      ? selectedValue.value
      : selectedValue;

    if (!propertyId || propertyId === activePropertyId) {
      return;
    }

    const source = [...(allProperties || []), ...(properties || [])];
    const selectedProperty = source.find((item) => item?.id === propertyId);

    if (selectedProperty) {
      localStorage.setItem('active_property', JSON.stringify(selectedProperty));
      dispatch(changeActiveProperty(selectedProperty));
    }
  };



  const renderOptionsProperty = () => {
    const dataSource = [...(allProperties || []), ...(properties || [])];
    const seen = new Set();
    const options = dataSource
      .filter((item) => {
        if (!item || !item.id || seen.has(item.id)) {
          return false;
        }
        seen.add(item.id);
        return true;
      })
      .map((item) => (
        <Option key={item.id} value={item.id} className="property-option">
          {item.name}
        </Option>
      ));

    return (
      <Select
        placeholder="Select Active Property"
        value={activePropertyId ?? undefined}
        size="large"
        className="custSelect"
        onChange={handleOptionClick}
        onPopupScroll={handlePopupScroll}
        dropdownRender={(menu) => (
          <div ref={menuRef} style={{ maxHeight: 320, overflow: 'auto' }}>
            {menu}
            <div className="dropdown-footer" style={{ textAlign: 'center', color: '#0B2A1B' }}>
              {loadingOptions ? <Spin size="small" indicator={greenIndicator} className="custom-spin" /> : null}
            </div>
          </div>
        )}
      >
        {options}
      </Select>
    );
  };

  const onSaved = () => {
    setIsSaved(!isSaved);
  }
  // Search and the date range are applied by the server (the same name / status /
  // updated_at / last_activity columns the browser used to filter on), so these
  // handlers only record the criteria and reset to the first page.
  const handleCancel = () => {
    setSelectedDateRange([]);
    setCurrentPage(1);
  };

  const handleDateRangeChange = (dates) => {
    setSelectedDateRange(dates || []);
    setCurrentPage(1);
  };

  const handleSearch = (e) => {
    setsearchInput(e.target.value);
  };

  const handleCancelSearch = () => {
    setsearchInput('');
  };
  return (
    <LayoutWrapper>
    <FormPage>
    <Box id="containerBox">
        <div className='manage-property-header'>
        <div className="mp-title">
              <h2 className="manage-property-title">Forms</h2>
            </div>
            <Input
                  placeholder="Search…"
                  size="large"
                  className="search-bar"
                  value={searchInput}
                  onChange={(e) => handleSearch(e)}
                  prefix={
                    !searchInput ? (
                      <>
                        <SearchOutlined className="site-form-item-icon" />
                      </>
                    ) : (
                      <CloseCircleOutlined
                        onClick={handleCancelSearch}
                        className="site-form-item-cancel"
                      />
                    )
                  }
                />

<div className="button-group" style={{ gap: "12px" }}>
            {(activePermission == "owner" || activePermission == "admin") && (
              <Button
                icon={<PlusOutlined />}
                type="primary"
                className="btn-success"
                onClick={showModal}
                size="large"
              >
                Add Form
              </Button>
            )}
            {(activePermission == "owner" || activePermission == "admin") && (
              <Button
                icon={<TeamOutlined />}
                onClick={() => setManageHostsVisible(true)}
                size="large"
              >
                Manage Hosts
              </Button>
            )}
            {!embedded && renderOptionsProperty()}
            <RangePicker
              value={selectedDateRange}
              onCancel={handleCancel}
            onChange={handleDateRangeChange} renderExtraFooter={() => 'extra footer'} className="startEndDate" />
          </div>

        </div>

        {renderCoverageBanner()}

        <div className="table_forms">
        {loadingData ? (
                <Loader/>
              ) : (
                <>
          {displayedForms && displayedForms.length > 0 ? (
            <>
              <Table
                columns={columns}
                rows={displayedForms}
                containerId="containerBox"
                showPagination={false}
                rowSize={99999}
              />
               <div style={{padding:'16px'}}>
                  <Pagination
                    current={currentPage}
                    total={totalCount}
                    pageSize={pageSize}
                    onChange={handlePageChange}
                    showSizeChanger
                    pageSizeOptions={["10", "20", "50", "100"]}
                    onShowSizeChange={handlePageSizeChange}
                  /></div>
              </>
          ) : (
            <h1 style={{ padding: "15px 25px", color: "gray" }}>
              <IntlMessages id="dashboardPage.noData" />
            </h1>
          )}
          </>
              )}
        </div>
      </Box>
      <FormModal visibleFormModal={visibleFormModal} cancelModal={cancelModal} propertyId={activePropertyId}
        updateFormData={updateFormData} formData={formData}
        />
        <InspectionModal
          visibleInspectionModal={visibleInspectionModal}
          cancelInspectionModal={cancelInspectionModal}
          formId={formData?.id}
          propertyId={formData?.property_id}
        />
        <ManageHostsModal
          visible={manageHostsVisible}
          propertyId={activePropertyId}
          onClose={() => setManageHostsVisible(false)}
        />
    </FormPage>
  </LayoutWrapper>
  );
}

export {Form};
